import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useRole } from '../../hooks/useRole';
import { UNSAVED_MESSAGE, useUnsavedChangesGuard } from '../../hooks/useUnsavedChangesGuard';
import { useNotifications } from '../NotificationProvider';
import { archivePerson, giveLogin, restorePerson, updatePerson } from '../../services/PeopleService';
import { removeLogin } from '../../services/PersonMembershipService';
import { errorMessageOf } from '../../utils/apiErrors';
import { getActiveProjectId } from '../../utils/session';
import { ROLE_LABELS } from '../../utils/roles';
import type { UserRole } from '../../types/User';
import type { Person, PersonInput } from '../../types/People';
import Button from '../ui/Button';
import ConfirmDialog from '../ui/ConfirmDialog';
import LoadErrorBox from '../ui/LoadErrorBox';
import EmptyState from '../EmptyState';
import LoadingSpinner from '../LoadingSpinner';
import AccountBar from './AccountBar';
import AccountFormPage from './AccountFormPage';
import PersonDetailsSection, { type PersonDetailsErrors, type PersonDetailsValues } from '../people/PersonDetailsSection';
import PersonLoginSection from '../people/PersonLoginSection';
import PersonRolesSection from '../people/PersonRolesSection';
import PersonProjectAccessSection from '../people/PersonProjectAccessSection';
import { useProjectAccess } from '../people/useProjectAccess';
import { findPerson, loadPerson, savePersonChanges, type LoadedPerson } from '../people/personEditorApi';
import { diffLines, hasChanges, reconcileAfterSave, validateLines, type RoleLine } from '../people/personLines';
import { workspaceProjects } from '../people/peopleView';
import '../../styles/components/AccountPage.css';
import '../../styles/components/People.css';
import '../../styles/components/EditPersonPage.css';

const LIST_PATH = '/workspace/users';
const EMAIL_SHAPE = /^\S+@\S+\.\S+$/;

const detailsOf = (p: Person): PersonDetailsValues => ({
  name: p.name, email: p.email ?? '', phone: p.phone ?? '', whatsapp: p.whatsapp ?? '', notes: p.notes ?? '',
});

/** The fields that differ from what was loaded, as the People route takes them. */
function changedDetails(initial: PersonDetailsValues, now: PersonDetailsValues): Partial<PersonInput> {
  const out: Partial<PersonInput> = {};
  if (now.name.trim() !== initial.name.trim()) out.name = now.name;
  if (now.email.trim().toLowerCase() !== initial.email.trim().toLowerCase()) out.email = now.email;
  for (const key of ['phone', 'whatsapp', 'notes'] as const) {
    if (now[key].trim() !== initial[key].trim()) out[key] = now[key];
  }
  return out;
}

function validateDetails(values: PersonDetailsValues, hasLogin: boolean): PersonDetailsErrors {
  const errors: PersonDetailsErrors = {};
  if (!values.name.trim()) errors.name = 'Enter a name.';
  const email = values.email.trim();
  if (email && !EMAIL_SHAPE.test(email)) errors.email = 'Enter a valid e-mail address.';
  else if (!email && hasLogin) errors.email = 'A person with a login must keep an e-mail address.';
  return errors;
}

/** A page-sized message with a way back to the Users list. */
const Notice: React.FC<{ icon: string; title: string; description: string; children?: React.ReactNode }> = ({ icon, title, description, children }) => (
  <div className="wt-container fade-in ap-page">
    <AccountBar view="user-edit" />
    <EmptyState icon={icon} title={title} description={description}
      action={<Link className="epp-link" to={LIST_PATH}>Back to Users</Link>} />
    {children}
  </div>
);

type Load =
  | { status: 'loading' }
  | { status: 'error'; error: unknown }
  | { status: 'notfound' }
  | { status: 'ready'; data: LoadedPerson };

/**
 * Edit user, a page of its own at /workspace/users/PE-<id>/edit: the person's details, their login (access role for
 * the whole workspace) and their role on each project as a list of lines. Save sends every change and reports each
 * failure where it happened; nothing is silently dropped. A role per project login does not exist (see the login
 * section's wording): the line roles are job roles on a project's team.
 */
const EditPersonPage: React.FC<{ personId: string | null }> = ({ personId }) => {
  const navigate = useNavigate();
  const { user, workspaces } = useAuth();
  const { isAdmin, isOwner, role: callerRole } = useRole();
  const { showError, showSuccess } = useNotifications();
  const savingRef = useRef(false);

  const [load, setLoad] = useState<Load>({ status: 'loading' });
  const [person, setPerson] = useState<Person | null>(null);
  const [teams, setTeams] = useState<string[]>([]);
  const [initialDetails, setInitialDetails] = useState<PersonDetailsValues | null>(null);
  const [details, setDetails] = useState<PersonDetailsValues | null>(null);
  const [initialLines, setInitialLines] = useState<RoleLine[]>([]);
  const [lines, setLines] = useState<RoleLine[]>([]);
  const [roleDraft, setRoleDraft] = useState<UserRole>('planner');
  const [detailsErrors, setDetailsErrors] = useState<PersonDetailsErrors>({});
  const [detailsServerError, setDetailsServerError] = useState<string | null>(null);
  const [roleError, setRoleError] = useState<string | null>(null);
  const [lineErrors, setLineErrors] = useState<Record<string, string>>({});
  const [summary, setSummary] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState<'archive' | 'remove-login' | null>(null);
  const [confirmError, setConfirmError] = useState<string | null>(null);

  const apply = useCallback((data: LoadedPerson, keep?: { details: PersonDetailsValues; lines: RoleLine[] }) => {
    setPerson(data.person);
    setTeams(data.teams);
    setInitialDetails(detailsOf(data.person));
    setDetails(keep?.details ?? detailsOf(data.person));
    setInitialLines(data.lines);
    setLines(keep?.lines ?? data.lines);
    setRoleDraft(data.person.login?.role ?? 'planner');
  }, []);

  const fetchAll = useCallback(async (isCurrent: () => boolean = () => true) => {
    setLoad({ status: 'loading' });
    try {
      const data = personId ? await loadPerson(personId) : null;
      if (!isCurrent()) return;
      if (!data) { setLoad({ status: 'notfound' }); return; }
      apply(data);
      setLoad({ status: 'ready', data });
    } catch (error) {
      if (isCurrent()) setLoad({ status: 'error', error: error ?? new Error('load failed') });
    }
  }, [personId, apply]);

  useEffect(() => {
    if (!isAdmin) return undefined;
    let current = true;
    void fetchAll(() => current);
    return () => { current = false; };
  }, [isAdmin, fetchAll]);

  const projects = useMemo(
    () => workspaceProjects(workspaces, getActiveProjectId()).filter(p => !p.archived_at),
    [workspaces],
  );

  const ready = load.status === 'ready' && person && details && initialDetails;
  const archived = !!person && person.archived_at !== null;
  const changes = useMemo(() => diffLines(initialLines, lines), [initialLines, lines]);
  const detailsPatch = ready ? changedDetails(initialDetails, details) : {};
  const detailsDirty = Object.keys(detailsPatch).length > 0;
  const roleChanged = !!person?.login && roleDraft !== person.login.role;
  const isMe = !!person?.login?.email && person.login.email.toLowerCase() === (user?.email ?? '').toLowerCase();
  // Access per project is the login's own, so it is offered for a login that is not the signed-in person's.
  const access = useProjectAccess(person?.login?.user_id ?? null, person?.login?.role ?? null, projects, !!person?.login && !isMe && !archived && isAdmin);
  const dirty = !!ready && !archived && (detailsDirty || roleChanged || hasChanges(changes) || access.dirty);
  useUnsavedChangesGuard(dirty && !saving);

  const leave = () => {
    if (dirty && !window.confirm(UNSAVED_MESSAGE)) return;
    navigate(LIST_PATH);
  };

  const handleSave = async () => {
    if (!ready || savingRef.current || archived) return;
    const detailProblems = validateDetails(details, !!person.login);
    const lineProblems = validateLines(lines);
    setDetailsErrors(detailProblems);
    setLineErrors(lineProblems);
    setDetailsServerError(null);
    setRoleError(null);
    setSummary(null);
    if (Object.keys(detailProblems).length > 0 || Object.keys(lineProblems).length > 0) {
      setSummary('Some things need fixing before this can be saved. They are marked below.');
      return;
    }
    savingRef.current = true;
    setSaving(true);
    try {
      const result = await savePersonChanges({
        personId: person.id,
        details: detailsPatch,
        loginRole: roleChanged && person.login ? { userId: person.login.user_id, role: roleDraft } : null,
        changes,
      });
      const accessResult = access.dirty ? await access.save() : { saved: 0, failed: 0, errors: {} };
      result.saved += accessResult.saved;
      result.failed += accessResult.failed;
      if (result.failed === 0) {
        showSuccess(`${details.name.trim()} saved.`);
        navigate(LIST_PATH);
        return;
      }
      let fresh: LoadedPerson | null = null;
      try { fresh = await loadPerson(person.id); } catch { /* said below */ }
      setDetailsServerError(result.detailsError);
      setRoleError(result.roleError);
      setLineErrors(result.lineErrors);
      const failedCount = result.failed;
      setSummary(`${result.saved} ${result.saved === 1 ? 'change was' : 'changes were'} saved and ${failedCount} could not be. The ones that failed stay on this page, marked; fix them and save again.`);
      if (fresh) {
        apply(fresh, {
          details: result.detailsError ? details : detailsOf(fresh.person),
          lines: reconcileAfterSave(lines, fresh.lines, result.failedRemovals),
        });
        if (result.roleError) setRoleDraft(roleDraft);
      } else {
        setSummary(s => `${s ?? ''} The page could not be refreshed, so reload it before saving again.`.trim());
      }
    } catch (err) {
      showError(errorMessageOf(err, "Couldn't save. Please try again."));
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  /** Refreshes only the login (and the saved e-mail) after giving or removing one, leaving unsaved edits alone. */
  const refreshLogin = async () => {
    if (!person) return;
    const { person: fresh } = await findPerson(person.id);
    if (!fresh) return;
    setPerson(fresh);
    setRoleDraft(fresh.login?.role ?? 'planner');
    setInitialDetails(detailsOf(fresh));
    setDetails(d => (d ? { ...d, email: fresh.email ?? '' } : d));
  };

  const handleGiveLogin = async (role: UserRole, email?: string) => {
    if (!person) return;
    try {
      if (email) await updatePerson(person.id, { email });
      const made = await giveLogin(person.id, role);
      const kept = made?.login?.role;
      showSuccess(kept && kept !== role
        ? `Login linked. A login for this e-mail already existed, so it keeps its role: ${ROLE_LABELS[kept] ?? kept}.`
        : `Login created for ${person.name}.`);
    } finally {
      await refreshLogin().catch(() => { /* the section keeps what it had */ });
    }
  };

  const runConfirmed = async (action: () => Promise<void>) => {
    setBusy(true);
    setConfirmError(null);
    try {
      await action();
      setConfirm(null);
    } catch (err) {
      setConfirmError(errorMessageOf(err, 'That did not go through. Please try again.'));
    } finally {
      setBusy(false);
    }
  };

  const handleRemoveLogin = () => runConfirmed(async () => {
    await removeLogin(person!.login!.user_id);
    showSuccess(`Login removed for ${person!.name}.`);
    await refreshLogin();
  });

  const handleArchive = () => runConfirmed(async () => {
    await archivePerson(person!.id);
    showSuccess(`${person!.name} archived.`);
    navigate(LIST_PATH);
  });

  const handleRestore = () => runConfirmed(async () => {
    await restorePerson(person!.id);
    showSuccess(`${person!.name} restored.`);
    await fetchAll();
  });

  if (!personId) {
    return <Notice icon="user" title="Person not found" description="This link does not point at a person. Go back to the Users list and pick one." />;
  }
  if (!isAdmin) {
    return <Notice icon="lock" title="You can't edit users" description="Only an owner or admin can change people and their roles. Ask an owner if you need something changed." />;
  }
  if (load.status === 'loading') {
    return (
      <div className="wt-container fade-in ap-page">
        <AccountBar view="user-edit" />
        <LoadingSpinner compact message="Loading person…" />
      </div>
    );
  }
  if (load.status === 'error') {
    return (
      <div className="wt-container fade-in ap-page">
        <AccountBar view="user-edit" />
        <LoadErrorBox what="this person" error={load.error} onRetry={() => void fetchAll()} />
        <Link className="epp-link" to={LIST_PATH}>Back to Users</Link>
      </div>
    );
  }
  if (!ready) {
    return <Notice icon="user" title="Person not found" description="This person does not exist, or they are not in your workspace." />;
  }

  const title = details.name.trim() || initialDetails.name || 'Person';

  return (
    <>
      <AccountFormPage
        view="user-edit"
        leaf={initialDetails.name}
        title={title}
        subtitle={archived ? 'Archived. Restore this person to edit them.' : 'Details, login and the role on each project.'}
        onSave={() => void handleSave()}
        onCancel={leave}
        canSave={dirty}
        saving={saving}
        readOnly={archived}
      >
        {summary && <p className="ep-summary" role="alert">{summary}</p>}
        <PersonDetailsSection
          values={details}
          errors={detailsErrors}
          serverError={detailsServerError}
          disabled={archived || saving}
          emailHint={person.login ? 'Only an owner can change the e-mail of a person who has a login.' : undefined}
          onChange={patch => { setDetails({ ...details, ...patch }); setDetailsErrors({}); }}
        />
        <PersonLoginSection
          person={person}
          isOwner={isOwner}
          isMe={isMe}
          readOnly={archived || saving}
          detailsDirty={detailsDirty}
          roleDraft={roleDraft}
          roleError={roleError}
          onRoleChange={setRoleDraft}
          onGiveLogin={handleGiveLogin}
          onRemoveLogin={() => { setConfirmError(null); setConfirm('remove-login'); }}
        />
        {person.login && !isMe && isAdmin && (
          <PersonProjectAccessSection
            load={access.load}
            rows={access.rows}
            errors={access.errors}
            callerRole={callerRole}
            disabled={archived || saving}
            onChoose={access.choose}
          />
        )}
        <PersonRolesSection
          lines={lines}
          onChange={next => { setLines(next); setLineErrors({}); }}
          projects={projects}
          knownTeams={teams}
          errors={lineErrors}
          disabled={archived || saving}
        />
        <section className="settings-card ep-section" aria-label="Archive">
          <h2 className="ap-section-title">{archived ? 'Restore' : 'Archive'}</h2>
          {archived ? (
            <div className="ep-actions">
              <Button type="button" variant="secondary" size="sm" loading={busy} onClick={() => void handleRestore()}>Restore person</Button>
              <span className="settings-muted-xs">Moves them back to the live list.</span>
            </div>
          ) : (
            <div className="ep-actions">
              <Button type="button" variant="ghost" size="sm" disabled={dirty || busy} onClick={() => { setConfirmError(null); setConfirm('archive'); }}>Archive person</Button>
              <span className="settings-muted-xs">
                {dirty ? 'Save or cancel your changes first.' : 'Moves them to Archived. Their login and project roles are not changed.'}
              </span>
            </div>
          )}
        </section>
      </AccountFormPage>

      <ConfirmDialog
        open={confirm === 'archive'}
        onClose={() => setConfirm(null)}
        onConfirm={() => void handleArchive()}
        title="Archive this person?"
        subject={person.name}
        description={person.login ? 'They move to Archived. Their login is not changed: the API asks you to deactivate it first if they can still sign in.' : 'They move to Archived and can be restored at any time.'}
        confirmLabel="Archive"
        variant="primary"
        error={confirmError}
        loading={busy}
      />
      <ConfirmDialog
        open={confirm === 'remove-login'}
        onClose={() => setConfirm(null)}
        onConfirm={() => void handleRemoveLogin()}
        title="Remove this login?"
        subject={person.name}
        consequences={['They can no longer sign in to this workspace.', 'They stay in the people directory and on their projects.']}
        confirmLabel="Remove login"
        error={confirmError}
        loading={busy}
      />
    </>
  );
};

export default EditPersonPage;
